import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { EmpresaConfigService } from '../../../services/configuracoes/empresa.service';
import { ContaService } from '../../../services/conta/conta.service';
import { DadosDeRecebimentoDTO } from '../../../models/configuracoes/empresa.model';

/**
 * Dados de recebimento (Bancard): a conta de destino dos pagamentos com QR Code
 * das vendas e dos pedidos do minishop.
 *
 * Tela write-only por decisão de segurança: o backend NUNCA devolve o código do
 * comércio nem as chaves. O que a tela consegue saber é apenas se já foi configurado
 * e quando — por isso os campos aparecem sempre vazios, mesmo após salvar.
 *
 * Só o administrador da empresa consegue salvar; para os demais o backend responde 403.
 *
 * A tela também escolhe PARA ONDE vai o dinheiro cobrado: uma conta por forma (QR Code e cartão).
 * É a decisão vizinha das chaves — aqui se diz com que cadastro se cobra e onde o dinheiro entra;
 * quais formas o cliente vê no checkout continua sendo assunto da tela do Mini Shop.
 */
@Component({
  selector: 'app-dados-de-recebimento',
  templateUrl: './dados-de-recebimento.component.html',
  styleUrls: ['./dados-de-recebimento.component.scss'],
})
export class DadosDeRecebimentoComponent implements OnInit {

  form: DadosDeRecebimentoDTO = {
    comercioId: null,
    sucursal: null,
    chavePublica: null,
    chavePrivada: null,
    chavePublicaTpago: null,
    chavePrivadaTpago: null,
  };

  /** QR Vendas já configurado (os valores em si nunca chegam até aqui). */
  configurado = false;
  atualizadoEm?: string;

  /** tpago (link de pagamento por cartão) já configurado. */
  tpagoConfigurado = false;
  tpagoAtualizadoEm?: string;

  carregando = false;
  salvando = false;
  formEnviado = false;

  /**
   * Contas onde o dinheiro cobrado entra, uma por forma.
   *
   * Salvas na hora da escolha, e não no botão Salvar: aquele exige as chaves, que a tela nunca
   * consegue reler — trocar só a conta obrigaria a digitar tudo de novo.
   */
  contas = {
    contaQrCodeId: null as number | null,
    contaTarjetaId: null as number | null,
  };

  /**
   * Contas de banco em guaranis (GET /conta/banco-guarani) — as únicas que a Bancard consegue
   * creditar: ela cobra em guaranis e deposita num banco.
   *
   * Lista vazia é resposta válida: a empresa ainda não tem uma conta assim, e a tela orienta a
   * cadastrar em vez de mostrar um combo vazio sem explicação.
   */
  contasBancariasGuarani: { label: string; value: number }[] = [];
  salvandoContas = false;

  /**
   * Último estado confirmado pelo servidor, para desfazer a escolha quando o salvamento falhar.
   * Não dá para ler `contas` na hora do erro: o `ngModel` já trocou o valor antes do salvamento.
   */
  private contasSalvas = {
    contaQrCodeId: null as number | null,
    contaTarjetaId: null as number | null,
  };

  constructor(
    private empresaConfigService: EmpresaConfigService,
    private contaService: ContaService,
    private messageService: MessageService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.carregarStatus();
    this.carregarContasBancariasEmGuarani();
  }


  private carregarContasBancariasEmGuarani(): void {
    this.contaService.listarContasBancariasEmGuarani().subscribe({
      next: (contas: any[]) => {
        this.contasBancariasGuarani = (contas || []).map(conta => ({
          label: conta.tituloDaConta,
          value: conta.id,
        }));
        this.descartarContasForaDaLista();
      },
      error: () => (this.contasBancariasGuarani = []),
    });
  }

  /**
   * Esquece a conta configurada que não é mais elegível.
   *
   * Empresas que escolheram a conta antes desta regra podem ter uma conta de outra moeda gravada:
   * o combo não a mostra, e mantê-la em memória faria o salvamento ser recusado pelo backend por
   * causa de um valor que o usuário nem vê. Some da tela; no banco ela só é trocada quando o
   * usuário escolher outra.
   */
  private descartarContasForaDaLista(): void {
    if (!this.contasBancariasGuarani.length) return;
    const elegivel = (id: number | null) =>
      id != null && this.contasBancariasGuarani.some(conta => conta.value === id);

    this.contas = {
      contaQrCodeId: elegivel(this.contas.contaQrCodeId) ? this.contas.contaQrCodeId : null,
      contaTarjetaId: elegivel(this.contas.contaTarjetaId) ? this.contas.contaTarjetaId : null,
    };
    this.contasSalvas = { ...this.contas };
  }

  /**
   * Grava a conta escolhida assim que o usuário escolhe.
   *
   * Rollback visual em caso de erro, como nos demais toggles de configuração: o combo volta ao
   * que estava para não sugerir que a escolha foi gravada.
   */
  salvarContas(forma: 'QR_CODE' | 'TARJETA'): void {
    if (this.salvandoContas) return;
    this.salvandoContas = true;

    // Só o campo que o usuário mexeu: o PATCH é merge, e reenviar o outro faria uma conta antiga
    // (de outra moeda, escolhida quando a regra era outra) derrubar o salvamento desta.
    // `0` limpa a conta no backend; `undefined` não altera.
    const payload = forma === 'QR_CODE'
      ? { contaQrCodeId: this.contas.contaQrCodeId ?? 0 }
      : { contaTarjetaId: this.contas.contaTarjetaId ?? 0 };

    this.empresaConfigService.patchContasDeRecebimento(payload).subscribe({
      next: (status) => {
        this.salvandoContas = false;
        this.contas = {
          contaQrCodeId: status?.contaQrCodeId ?? null,
          contaTarjetaId: status?.contaTarjetaId ?? null,
        };
        this.contasSalvas = { ...this.contas };
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('dados-de-recebimento.texts.contas_salvas'),
        });
      },
      error: (err) => {
        this.salvandoContas = false;
        this.contas = { ...this.contasSalvas };
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: err?.error?.message || this.translate.instant('dados-de-recebimento.texts.erro_salvar_contas'),
        });
      },
    });
  }


  carregarStatus(): void {
    this.carregando = true;
    this.empresaConfigService.getDadosDeRecebimento().subscribe({
      next: (status) => {
        this.carregando = false;
        this.configurado = status?.configurado === true;
        this.atualizadoEm = status?.atualizadoEm;
        this.tpagoConfigurado = status?.tpagoConfigurado === true;
        this.tpagoAtualizadoEm = status?.tpagoAtualizadoEm;
        this.contas = {
          contaQrCodeId: status?.contaQrCodeId ?? null,
          contaTarjetaId: status?.contaTarjetaId ?? null,
        };
        this.contasSalvas = { ...this.contas };
        this.descartarContasForaDaLista();
      },
      error: () => {
        this.carregando = false;
        this.configurado = false;
        this.tpagoConfigurado = false;
      },
    });
  }

  get formValido(): boolean {
    return (
      this.form.comercioId != null &&
      this.form.sucursal != null &&
      !!this.form.chavePublica?.trim() &&
      !!this.form.chavePrivada?.trim()
    );
  }

  salvar(): void {
    this.formEnviado = true;
    if (this.salvando) return;

    if (!this.formValido) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.translate.instant('dados-de-recebimento.texts.preencha_todos'),
      });
      return;
    }

    if (this.tpagoIncompleto) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.translate.instant('dados-de-recebimento.texts.tpago_incompleto'),
      });
      return;
    }

    this.salvando = true;
    this.empresaConfigService.salvarDadosDeRecebimento(this.form).subscribe({
      next: (status) => {
        this.salvando = false;
        this.formEnviado = false;
        this.configurado = status?.configurado === true;
        this.atualizadoEm = status?.atualizadoEm;
        this.tpagoConfigurado = status?.tpagoConfigurado === true;
        this.tpagoAtualizadoEm = status?.tpagoAtualizadoEm;
        this.contas = {
          contaQrCodeId: status?.contaQrCodeId ?? null,
          contaTarjetaId: status?.contaTarjetaId ?? null,
        };
        this.contasSalvas = { ...this.contas };
        // Limpa os campos: eles não podem ser relidos do servidor, então manter o
        // que foi digitado na tela daria a falsa impressão de que dá para conferir depois.
        this.limparForm();
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('dados-de-recebimento.texts.salvo'),
        });
      },
      error: (err) => {
        this.salvando = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: err?.error?.message || this.translate.instant('dados-de-recebimento.texts.erro_salvar'),
        });
      },
    });
  }

  /**
   * O par do tpago é opcional, mas tem de vir COMPLETO: uma chave sem a outra não autentica, e o
   * backend ignoraria em silêncio.
   */
  get tpagoIncompleto(): boolean {
    const publica = !!this.form.chavePublicaTpago?.trim();
    const privada = !!this.form.chavePrivadaTpago?.trim();
    return publica !== privada;
  }

  limparForm(): void {
    this.form = {
      comercioId: null,
      sucursal: null,
      chavePublica: null,
      chavePrivada: null,
      chavePublicaTpago: null,
      chavePrivadaTpago: null,
    };
    this.formEnviado = false;
  }
}
